/**
 * Document Parser Utility
 * Extracts text from PDF files, image OCR (handwritten notes), and plain text files.
 * Features built-in fallbacks to ensure robust operation in all environments.
 */

// Global CDN loaders for dynamic script injection if packages are absent
async function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Parses text from PDF file page-by-page.
 */
export async function parsePdfFile(file, onProgress) {
  try {
    if (onProgress) onProgress({ stage: 'Reading PDF contents...' });
    
    let pdfjs = window.pdfjsLib;

    if (!pdfjs) {
      try {
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js');
        pdfjs = window.pdfjsLib;
        if (pdfjs) {
          pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }
      } catch (e) {
        console.warn("CDN fallback for PDF.js failed:", e);
      }
    }

    if (pdfjs) {
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      const numPages = pdf.numPages;
      const extractedPages = [];

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        if (onProgress) {
          onProgress({ current: pageNum, total: numPages, stage: `Reading PDF page ${pageNum}/${numPages}` });
        }
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item) => item.str).join(' ');
        if (pageText.trim()) {
          extractedPages.push({ pageNumber: pageNum, text: pageText.trim() });
        }
      }
      if (extractedPages.length > 0) return extractedPages;
    }

    // Secondary Fallback: Direct Binary Regex Extraction for text streams
    const text = await file.text();
    const cleanText = text.replace(/[^\x20-\x7E\n\r]/g, ' ').replace(/\s+/g, ' ');
    if (cleanText.trim().length > 100) {
      return [{ pageNumber: 1, text: cleanText.trim() }];
    }

    throw new Error('Could not extract text from PDF.');
  } catch (error) {
    console.error("PDF Parsing Error:", error);
    throw new Error(`Failed to parse PDF: ${error.message || 'Corrupt or protected file'}`);
  }
}

/**
 * Performs OCR on image files (handwritten notes).
 */
export async function parseImageFile(file, onProgress) {
  try {
    if (onProgress) onProgress({ stage: 'Initializing OCR Engine for Note Image...' });

    let Tesseract = window.Tesseract;
    if (!Tesseract) {
      try {
        await loadScript('https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js');
        Tesseract = window.Tesseract;
      } catch (e) {
        console.warn("CDN fallback for Tesseract failed:", e);
      }
    }

    if (Tesseract) {
      const worker = await Tesseract.createWorker('eng');
      const ret = await worker.recognize(file);
      await worker.terminate();

      const text = ret.data?.text || '';
      if (text.trim()) {
        return [{ pageNumber: 1, text: text.trim() }];
      }
    }

    throw new Error('OCR engine unavailable or no legible text found in image.');
  } catch (error) {
    console.error("Image OCR Error:", error);
    throw new Error(`OCR Processing Failed: ${error.message || 'Could not process image'}`);
  }
}

/**
 * Parses raw text or markdown files.
 */
export async function parseTextFile(file) {
  const text = await file.text();
  return [{ pageNumber: 1, text: text.trim() }];
}

/**
 * Master parser dispatching based on file extension / MIME type.
 */
export async function parseUploadedDocument(file, onProgress) {
  const name = file.name.toLowerCase();

  if (name.endsWith('.pdf')) {
    return parsePdfFile(file, onProgress);
  } else if (/\.(png|jpe?g|webp|bmp)$/i.test(name) || file.type.startsWith('image/')) {
    return parseImageFile(file, onProgress);
  } else {
    return parseTextFile(file);
  }
}

/**
 * Prepares semantic text chunks for LLM generation.
 */
export function prepareTextChunks(pages = [], maxChunkChars = 3500) {
  const chunks = [];
  let currentChunkText = '';
  let currentPages = [];

  for (const p of pages) {
    const pageHeader = `\n--- [Source Page / Slide ${p.pageNumber}] ---\n`;
    const candidateLength = currentChunkText.length + pageHeader.length + p.text.length;

    if (candidateLength > maxChunkChars && currentChunkText.trim()) {
      chunks.push({
        text: currentChunkText.trim(),
        pages: [...currentPages],
      });
      currentChunkText = pageHeader + p.text;
      currentPages = [p.pageNumber];
    } else {
      currentChunkText += pageHeader + p.text;
      if (!currentPages.includes(p.pageNumber)) {
        currentPages.push(p.pageNumber);
      }
    }
  }

  if (currentChunkText.trim()) {
    chunks.push({
      text: currentChunkText.trim(),
      pages: [...currentPages],
    });
  }

  return chunks;
}

